import { isCommodity, type Commodity } from '@enmon/commons/lib/shared';
import type { RadioChangeEvent, RadioGroupProps } from 'antd';
import { useEffect, type VFC } from 'react';
import styled, { css } from 'styled-components';
import { useCommodityOptions } from '@/hooks/useCommodityOptions';
import { Radio } from './Radio';
import { getBoxGroupStyle } from './utils/getBoxGroupStyle';

const StyledRadioGroup = styled<VFC<RadioGroupProps & { $compact: boolean }>>(Radio.Group)`
  ${({ $compact }) =>
    css`
      ${getBoxGroupStyle('radio', $compact)}
    `}
`;

interface CommoditiesRadioProps {
  allowed?: Commodity[];
  onlyConsumption?: boolean;
  value?: Commodity[];
  compact?: boolean;
  autoSelectSingleOption?: boolean;
  onChange?: (value: Commodity[] | undefined) => void;
}

export const CommoditiesRadio: VFC<CommoditiesRadioProps> = props => {
  const {
    allowed = null,
    onlyConsumption = true,
    autoSelectSingleOption = false,
    value,
    compact = true,
    onChange,
  } = props;

  const options = useCommodityOptions(allowed, onlyConsumption, value, compact ? 'icon' : 'tile');

  const handleChange = (e: RadioChangeEvent) => {
    if (isCommodity(e.target.value)) {
      onChange?.([e.target.value]);
    }
  };

  useEffect(() => {
    const availableCommodities = options.map(option => option.value);

    // If some commodities are set and are not in available commodities, reset this field
    if (value?.some(commodity => !availableCommodities.includes(commodity))) {
      onChange?.(undefined);
    }

    // If there is only one option, select it automatically
    if (autoSelectSingleOption && availableCommodities.length === 1) {
      if (!value || (value && value[0] !== availableCommodities[0])) {
        const commodity = availableCommodities[0];
        if (commodity) {
          onChange?.([commodity]);
        }
      }
    }
  }, [autoSelectSingleOption, options, value, onChange]);

  return (
    <StyledRadioGroup $compact={compact} options={options} value={value ? value[0] : null} onChange={handleChange} />
  );
};
